import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../lib/axios';
import { useProjects } from '../hooks/useProjects';
import { useContacts } from '../hooks/useContacts';
import useProjectStore from '../stores/projectStore';
import StatCard from '../components/ui/StatCard';

function useGlobalProjectReports() {
  return useQuery({
    queryKey: ['project-reports', 'global'],
    queryFn: async () => {
      const { data } = await api.get('/project-reports', { skipProject: true });
      return data.reports || [];
    },
  });
}

function useAnalyticsOverview() {
  return useQuery({
    queryKey: ['analytics-overview'],
    queryFn: async () => {
      const { data } = await api.get('/analytics/overview', { skipProject: true });
      return data;
    },
  });
}

function ProjectTile({ project, reports, onOpen }) {
  const latest = reports.find((r) => r.project_id === project.id);
  const cro = latest?.data?.cro || {};
  const seo = latest?.data?.seo || {};

  return (
    <button
      onClick={onOpen}
      className="bg-white rounded-xl shadow-sm border border-black/[.07] p-4 text-left hover:shadow-md hover:border-teal/30 transition-all"
    >
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center text-[14px] font-bold text-white shrink-0"
          style={{ backgroundColor: project.color || '#0C9488' }}
        >
          {(project.name || '?')
            .split(/\s+/)
            .map((s) => s[0])
            .slice(0, 2)
            .join('')
            .toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <div className="font-semibold text-[14px] text-gray-900 truncate">{project.name}</div>
            {project.is_default && (
              <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">
                DEFAULT
              </span>
            )}
          </div>
          {project.domain && (
            <div className="text-[11px] text-gray-400 truncate font-mono">{project.domain}</div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-gray-50 rounded-md py-1.5 text-center">
          <div className="text-[14px] font-bold text-gray-900">{project.analyses_count || 0}</div>
          <div className="text-[9.5px] text-gray-500 uppercase tracking-wide">CRO</div>
        </div>
        <div className="bg-gray-50 rounded-md py-1.5 text-center">
          <div className="text-[14px] font-bold text-gray-900">{project.seo_keywords_count || 0}</div>
          <div className="text-[9.5px] text-gray-500 uppercase tracking-wide">Keywords</div>
        </div>
        <div className="bg-gray-50 rounded-md py-1.5 text-center">
          <div className="text-[14px] font-bold text-gray-900">{project.seo_measures_count || 0}</div>
          <div className="text-[9.5px] text-gray-500 uppercase tracking-wide">Maßnahmen</div>
        </div>
      </div>

      {latest ? (
        <div className="pt-2 border-t border-gray-50 text-[11px] text-gray-500 flex items-center justify-between">
          <span>
            Letzter Report: <span className="text-gray-700 font-medium">{latest.data?.period?.label || '—'}</span>
          </span>
          {cro.avg_score != null && (
            <span className="text-teal font-semibold">Ø {cro.avg_score}</span>
          )}
        </div>
      ) : (
        <div className="pt-2 border-t border-gray-50 text-[11px] text-gray-400">
          Noch kein Monatsreport
        </div>
      )}
    </button>
  );
}

export default function GlobalOverview() {
  const navigate = useNavigate();
  const setCurrentProject = useProjectStore((s) => s.setCurrentProject);
  const { data: projectData, isLoading: projectsLoading } = useProjects();
  const { data: contactsData } = useContacts();
  const { data: reports = [] } = useGlobalProjectReports();
  const { data: analytics } = useAnalyticsOverview();

  const projects = projectData?.projects || [];
  const contacts = contactsData?.data || [];
  const crmContacts = contacts.filter((c) => c.crm_contact_id);

  const totalAnalyses = projects.reduce((s, p) => s + (p.analyses_count || 0), 0);
  const totalKeywords = projects.reduce((s, p) => s + (p.seo_keywords_count || 0), 0);
  const reportsCount = reports.length;

  const handleOpenProject = (id) => {
    setCurrentProject(id);
    navigate('/');
  };

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-[23px] font-bold mb-0.5">Global Overview</h1>
        <p className="text-gray-500 text-[13.5px]">Kunden, Projekte und Aktivität auf einen Blick</p>
      </div>

      <div className="flex gap-3.5 mb-6 flex-wrap">
        <StatCard
          label="Kunden"
          value={contacts.length || '—'}
          sub={crmContacts.length ? `${crmContacts.length} aus CRM` : 'aktive Kunden'}
          icon="◉"
          iconBg="#E6F4F2"
          iconColor="#0C9488"
        />
        <StatCard
          label="Projekte"
          value={projects.length || '—'}
          sub={`${projects.filter((p) => p.is_default).length} Default`}
          icon="⌘"
          iconBg="#DBEAFE"
          iconColor="#3B82F6"
        />
        <StatCard
          label="Analysen gesamt"
          value={totalAnalyses || '—'}
          sub={analytics?.analyses_30d != null ? `${analytics.analyses_30d} in 30 Tagen` : ''}
          icon="⟳"
          iconBg="#EDE9FE"
          iconColor="#8B5CF6"
        />
        <StatCard
          label="Keywords tracked"
          value={totalKeywords || '—'}
          sub="über alle Projekte"
          icon="◎"
          iconBg="#FEF3C7"
          iconColor="#B45309"
        />
        <StatCard
          label="Monatsreports"
          value={reportsCount || '—'}
          sub="erzeugt"
          icon="▦"
          iconBg="#D1FAE5"
          iconColor="#10B981"
        />
        <StatCard
          label="Tokens (30T)"
          value={analytics?.token_usage ? Number(analytics.token_usage).toLocaleString('de-DE') : '—'}
          sub={analytics?.pdf_exports != null ? `${analytics.pdf_exports} PDF-Exporte` : ''}
          icon="◈"
          iconBg="#FEE2E2"
          iconColor="#DC2626"
        />
      </div>

      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-[15px]">Projekte</h2>
        <button
          onClick={() => navigate('/projects')}
          className="text-[12px] text-teal font-medium hover:underline"
        >
          Projekte verwalten →
        </button>
      </div>

      {projectsLoading ? (
        <div className="py-12 text-center text-gray-400 text-sm">Wird geladen…</div>
      ) : projects.length === 0 ? (
        <div className="bg-white rounded-xl border border-black/[.07] p-10 text-center text-gray-500 text-sm">
          Noch keine Projekte angelegt.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {projects.map((p) => (
            <ProjectTile
              key={p.id}
              project={p}
              reports={reports}
              onOpen={() => handleOpenProject(p.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
